/**
 * AssigneeSelector Component
 *
 * Dropdown component for selecting a team member to assign to a task/clip/shot.
 * Shows project team members with their avatars and names.
 */

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useProjectMembers } from '@/hooks/useProjectMembers'
import { useAuth } from '@/contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Loader2 } from 'lucide-react'

interface AssigneeSelectorProps {
  projectId: string
  value: string | null | undefined
  onValueChange: (value: string | null) => void
  placeholder?: string
  disabled?: boolean
}

export function AssigneeSelector({
  projectId,
  value,
  onValueChange,
  placeholder = 'Select team member',
  disabled = false,
}: AssigneeSelectorProps) {
  const { user } = useAuth()
  const { data: members, isLoading: membersLoading } = useProjectMembers(projectId)

  // Also fetch current user's profile to include as an option
  const { data: currentUserProfile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle()
      if (error) throw error
      return data
    },
    enabled: !!user?.id,
  })

  const isLoading = membersLoading || profileLoading

  const handleValueChange = (newValue: string) => {
    if (newValue === 'unassigned') {
      onValueChange(null)
    } else {
      onValueChange(newValue)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-muted/50">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm text-muted-foreground">Loading team members...</span>
      </div>
    )
  }

  // Check if current user is in the members list
  const currentUserIsMember = members?.some(m => m.user_id === user?.id)

  // If current user is not a member but has a profile, add them to the list
  const allMembers = [...(members || [])]
  if (!currentUserIsMember && currentUserProfile && user?.id) {
    allMembers.push({
      project_id: projectId,
      user_id: user.id,
      role: 'viewer' as const,
      invited_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      profile: currentUserProfile,
    })
  }

  const selectedMember = allMembers?.find((m: any) => m.user_id === value)

  return (
    <Select
      value={value || 'unassigned'}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <SelectTrigger className="w-full">
        <SelectValue>
          {value && selectedMember ? (
            <div className="flex items-center gap-2">
              <Avatar className="h-5 w-5">
                <AvatarImage src={selectedMember.profile?.avatar_url} />
                <AvatarFallback className="text-xs">
                  {selectedMember.profile?.full_name?.charAt(0).toUpperCase() ||
                   selectedMember.profile?.email?.charAt(0).toUpperCase() || '?'}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm">
                {selectedMember.profile?.full_name || selectedMember.profile?.email || 'Unknown'}
              </span>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">{placeholder}</span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="unassigned">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center">
              <span className="text-xs text-muted-foreground">-</span>
            </div>
            <span className="text-sm text-muted-foreground">Unassigned</span>
          </div>
        </SelectItem>

        {allMembers?.map((member: any) => (
          <SelectItem key={member.user_id} value={member.user_id}>
            <div className="flex items-center gap-2">
              <Avatar className="h-5 w-5">
                <AvatarImage src={member.profile?.avatar_url} />
                <AvatarFallback className="text-xs">
                  {member.profile?.full_name?.charAt(0).toUpperCase() ||
                   member.profile?.email?.charAt(0).toUpperCase() || '?'}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm">
                {member.profile?.full_name || member.profile?.email || 'Unknown'}
                {member.user_id === user?.id && ' (You)'}
              </span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
